import { useRefresh } from "lib/@hooks/useRefresh"
import { Bound } from "lib/@components/binding/Bound"
import { Button, CircularProgress, Stack, Typography } from "@mui/material"
import { Frequency } from "routes/schedule/tasks/frequency"
import { Time } from "routes/schedule/tasks/time"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { updateZeroU } from "routes/regime/plugins/zero-u-wizard/controller/update-zero-u"
import {
    ApplyChanged,
    BatchUpdateCompleted,
    BatchUpdateStarted,
    CanApply,
    CollectPendingChanges,
    TaskUpdated,
} from "routes/regime/plugins/zero-u-wizard/apply-all-button"
import { useEffect, useState } from "react"
import { confirmByUserInput } from "lib/@dialogs/confirm-by-user-input"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { SuppressTreeBusy } from "routes/regime/plugins/zero-u-wizard/suppress-tree-busy"
import { RegimeDraftLockHide } from "routes/regime/regime-draft-locking/regime-draft-lock-hide"

export function Tailor0U({ task, skill, schedule, count }) {
    if (schedule === "-") schedule = undefined
    if (skill === "-") skill = undefined
    const [disabled, setDisabled] = useState(false)

    const refresh = useRefresh()
    const { search, group, regimeId, target } = useBoundContext()
    const canSave = target.minutes !== 0 && target.frequency.period !== "Unknown" && target.frequency.interval > 0
    const filters = [
        { "task.title": task },
        skill && { skill: skill },
        schedule && { "schedule._id": schedule },
    ].filter(Boolean)

    // Report whether this row can be applied (for Apply All button state)
    CanApply.useEvent(
        ({ canApply, tasksForCount }) => ({
            canApply: !disabled && (canApply || canSave),
            tasksForCount: [...tasksForCount, canSave && task ? task : null],
        }),
        [canSave, disabled]
    )

    // Provide pending change data for batch updates (Apply All)
    CollectPendingChanges.useEvent(
        ({ updates }) => {
            if (canSave && !disabled) {
                return {
                    updates: [
                        ...updates,
                        {
                            task,
                            schedule,
                            skill,
                            interval: target.frequency.interval,
                            period: target.frequency.period,
                            minutes: target.minutes,
                        },
                    ],
                }
            }
            return { updates }
        },
        [canSave, disabled, task, target, schedule, skill]
    )

    // Handle batch update starting - show spinner if this row is being updated
    BatchUpdateStarted.useEvent(
        ({ updates }) => {
            if (canSave) {
                // Match by task, schedule, and skill to only disable the exact row being updated
                const isBeingUpdated = updates.some(
                    (u) => u.task === task && u.schedule === schedule && u.skill === skill
                )
                if (isBeingUpdated) {
                    setDisabled(true)
                }
            }
        },
        [task, schedule, skill, canSave]
    )

    // Handle batch update completion - keep disabled if successful, reset if failed
    BatchUpdateCompleted.useEvent(({ failed }) => {
        if (failed) {
            // Reset state on failure so user can try again
            setDisabled(false)
        }
        // If successful, row stays disabled (will be removed from list)
    }, [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => ApplyChanged.raiseOnce(), [canSave])

    return (
        <Bound refresh={refresh}>
            <Stack sx={{ width: 1 }}>
                <If not truthy={disabled}>
                    <Stack direction="row" sx={{ pt: 2, pb: 2 }}>
                        <Frequency />
                        <Time sx={{ width: 100 }} />

                        <RegimeDraftLockHide>
                            <Button
                                data-cy="apply-button"
                                startIcon={<Iconify icon="mdi:text-box-check" />}
                                sx={{ ml: 1 }}
                                size="small"
                                disabled={!canSave}
                                color="primary"
                                onClick={update}
                            >
                                <Typography variant="caption" sx={{ visibility: canSave ? undefined : "hidden" }}>
                                    Apply
                                </Typography>
                            </Button>
                        </RegimeDraftLockHide>
                    </Stack>
                    <Else>
                        <Stack
                            direction="row"
                            sx={{
                                pt: 2,
                                pb: 2,
                                alignItems: "center",
                                gap: 1,
                                width: 1,
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={24} color="primary" />
                            <Typography variant="caption">Applying...</Typography>
                        </Stack>
                    </Else>
                </If>
            </Stack>
        </Bound>
    )

    async function update() {
        if (
            await confirmByUserInput({
                title: "Are you sure you want to tailor these tasks?",
                confirmValue: count,
                startIcon: "mdi:text-box-check",
            })
        ) {
            await save()
        }
    }

    async function save() {
        if (canSave && !disabled) {
            SuppressTreeBusy.raise(true)
            try {
                setDisabled(true)
                await updateZeroU({
                    regimeId,
                    group,
                    search,
                    filters,
                    ...target.frequency,
                    minutes: target.minutes,
                })
                // Signal that this task was updated so it can be removed from the list immediately
                TaskUpdated.raise({ task, skill, schedule })
            } catch {
                errorSnackbar("Failed to update Unspecified Frequencies")
                setDisabled(false)
            } finally {
                SuppressTreeBusy.raise(false)
            }
        }
    }
}
